import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  Search01Icon, 
  FilterHorizontalIcon,
  ArrowLeft01Icon,
  UserIcon,
  ViewIcon,
  Delete01Icon
} from '@hugeicons/core-free-icons';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data
  const users = Array.from({ length: 10 }, (_, i) => ({
    id: `usr_${i + 100}`,
    name: `User ${i + 1}`,
    email: `user_${i + 1}@example.com`,
    role: i % 3 === 0 ? 'agent' : 'client',
    status: i % 5 === 0 ? 'suspended' : 'active',
    joined_at: new Date(Date.now() - Math.random() * 10000000000).toISOString()
  }));

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/admin')}
            className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={24} className="text-charcoal dark:text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-charcoal dark:text-white">User Management</h1>
            <p className="text-muted-gray">Manage registered users and agents</p>
          </div>
        </div>
        <button className="px-4 py-2 bg-soft-green text-white rounded-xl font-bold hover:bg-light-mint transition-colors">
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-surface p-4 rounded-2xl border border-light-gray dark:border-dark-border shadow-sm mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <HugeiconsIcon icon={Search01Icon} size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-gray" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-off-white/50 dark:bg-gray-800/50 border border-light-gray dark:border-gray-700 rounded-xl focus:ring-soft-green focus:border-soft-green outline-none text-charcoal dark:text-white"
          />
        </div>
        <button className="flex items-center justify-center px-4 py-2 border border-light-gray dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 text-charcoal dark:text-white">
          <HugeiconsIcon icon={FilterHorizontalIcon} size={20} className="mr-2" />
          Filter
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-dark-surface rounded-2xl border border-light-gray dark:border-dark-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-light-gray dark:border-gray-700">
              <tr>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">Joined</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light-gray dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <tr 
                  key={user.id} 
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-soft-green/10 rounded-full flex items-center justify-center">
                        <HugeiconsIcon icon={UserIcon} size={20} className="text-soft-green" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-bold text-charcoal dark:text-white">{user.name}</div>
                        <div className="text-xs text-muted-gray">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-bold rounded-full uppercase ${
                      user.role === 'agent' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-bold rounded-full uppercase ${
                      user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-gray">
                    {new Date(user.joined_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-3">
                      <button className="text-gray-400 hover:text-soft-green">
                        <HugeiconsIcon icon={ViewIcon} size={20} />
                      </button>
                      <button 
                        className="text-gray-400 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle delete logic
                        }}
                      >
                        <HugeiconsIcon icon={Delete01Icon} size={20} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
